import MuiCircularProgress from '@mui/material/CircularProgress';
import * as React from 'react';

import { FlexBox } from '../box';
import { Chip } from '../chip';
import { useMounted } from '../hooks';
import { IconLocationOn } from '../icon';
import { Skeleton } from '../skeleton';
import { usePaletteColor } from '../theme';
import { Tooltip } from '../tooltip';
import { FacilityInfoDtoLike } from '.';

type FacilityBadgeActivityProps =
    | { inactive: true; inactiveTooltipText: string }
    | { inactive?: false; inactiveTooltipText?: never };

export type FacilityBadgeProps =
    | ({
          facilityInfo: FacilityInfoDtoLike;
          facilityName?: never;
          moreText(more: number): string;
          loading?: boolean;
      } & FacilityBadgeActivityProps)
    | {
          facilityInfo?: never;
          facilityName: string;
          moreText?: never;
          loading?: boolean;
          inactive?: never;
          inactiveTooltipText?: never;
      };

export interface AsyncFacilityBadgeProps {
    label: React.ReactNode;
    onFetch(): Promise<FacilityInfoDtoLike | undefined>;
    moreText(more: number): string;

    /**
     * Whether to use the cache for the facility info.
     * @default true
     */
    useCache?: boolean;
}

interface InternalFacilityBadgeProps {
    tooltipContent?: React.ReactNode;
    chipLabel?: React.ReactNode;
    inactive?: boolean;
    onVisibilityChange?(visible: boolean): void;
}

function buildTooltipContent(
    facilityInfo: FacilityInfoDtoLike | undefined,
    moreText?: (more: number) => string,
    inactiveTooltipText?: string
) {
    if (inactiveTooltipText !== undefined) {
        return inactiveTooltipText;
    }

    if (!facilityInfo) {
        return undefined;
    }

    const MAX_STUDIOS = 6;
    const { sharedFacilities = 0, whitelistEntries = [] } = facilityInfo;
    const facilities = whitelistEntries
        .filter(({ permission }) => permission !== 'NONE')
        .slice(0, MAX_STUDIOS)
        .map(({ facilityName, name }) => facilityName ?? name ?? '');

    const title = (() => {
        const list: string[] = [...facilities];
        const more = Math.max(0, sharedFacilities - facilities.length);

        if (more && moreText) {
            list.push(moreText(more));
        }

        return list.join('\n');
    })();

    return title;
}

const InternalFacilityBadge = (props: InternalFacilityBadgeProps) => {
    const [tooltipVisible, setTooltipVisibility] = React.useState(false);

    const colorText = usePaletteColor('components.facility.contrast.text');
    const colorIcon = usePaletteColor('components.facility.contrast.icon');
    const colorTextInactive = usePaletteColor(
        'components.facility.contrast.text-inactive'
    );
    const colorIconInactive = usePaletteColor(
        'components.facility.contrast.icon-inactive'
    );
    const bgHover = usePaletteColor('components.facility.background.hover');
    const bgDefault = usePaletteColor('components.facility.background.enabled');
    const bgInactive = usePaletteColor(
        'components.facility.background.inactive'
    );

    const onVisibilityChange = (visibility: boolean) => {
        setTooltipVisibility(visibility);
        props.onVisibilityChange?.(visibility);
    };

    const bgColor = props.inactive
        ? bgInactive
        : tooltipVisible
          ? `${bgHover} !important`
          : bgDefault;
    const textColor = props.inactive ? colorTextInactive : colorText;
    const iconColor = props.inactive ? colorIconInactive : colorIcon;

    return (
        <Tooltip
            title={props.tooltipContent}
            onVisibilityChange={onVisibilityChange}
            disabled={!props.tooltipContent}
        >
            <Chip
                label={props.chipLabel}
                icon={<IconLocationOn />}
                sx={{
                    color: textColor,
                    bgcolor: bgColor,
                    '.MuiChip-icon': {
                        color: iconColor
                    }
                }}
            />
        </Tooltip>
    );
};

export const FacilityBadge = (props: FacilityBadgeProps) => {
    const {
        moreText,
        facilityName,
        facilityInfo,
        loading,
        inactive,
        inactiveTooltipText
    } = props;

    if (loading) {
        return <Skeleton width={42} height={24} />;
    }

    if (facilityName) {
        return (
            <InternalFacilityBadge
                chipLabel={facilityName}
                inactive={inactive}
            />
        );
    }

    const { sharedFacilities = 0, whitelistEntries = [] } = facilityInfo ?? {};

    const tooltipContent = buildTooltipContent(
        { sharedFacilities, whitelistEntries },
        moreText,
        inactive ? inactiveTooltipText : undefined
    );

    return (
        <InternalFacilityBadge
            tooltipContent={tooltipContent}
            chipLabel={inactive ? '0' : sharedFacilities}
            inactive={inactive}
        />
    );
};
FacilityBadge.displayName = 'FacilityBadge';

const AsyncFacilityBadge = ({
    label,
    moreText,
    onFetch,
    useCache = true
}: AsyncFacilityBadgeProps) => {
    const [facilityInfo, setFacilityInfo] = React.useState<
        FacilityInfoDtoLike | undefined
    >(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const mounted = useMounted();

    const onVisibilityChange = (visible: boolean) => {
        if (facilityInfo && useCache) {
            return;
        }

        if (!isLoading && facilityInfo && useCache) {
            return;
        }

        if (!visible) {
            return;
        }

        setIsLoading(true);

        void (async () => {
            try {
                const info = await onFetch();
                if (!mounted.current) {
                    return;
                }
                setFacilityInfo(info);
            } finally {
                if (mounted.current) {
                    setIsLoading(false);
                }
            }
        })();
    };

    const tooltipContent = (() => {
        if (isLoading) {
            return (
                <FlexBox vAlign="center">
                    <MuiCircularProgress
                        role="loading"
                        size={16}
                        sx={{ color: 'inherit' }}
                    />
                </FlexBox>
            );
        }

        if (facilityInfo === null) {
            return undefined;
        }

        return buildTooltipContent(facilityInfo, moreText);
    })();

    return (
        <InternalFacilityBadge
            tooltipContent={tooltipContent}
            chipLabel={label}
            onVisibilityChange={onVisibilityChange}
        />
    );
};

AsyncFacilityBadge.displayName = 'FacilityBadge.Async';
FacilityBadge.Async = AsyncFacilityBadge;

export interface SingleFacilityBadgeProps {
    facilityName?: string;
}

export const SingleFacilityBadge = ({
    facilityName
}: SingleFacilityBadgeProps) => (
    <InternalFacilityBadge tooltipContent={facilityName} />
);
